import { useCallback } from "react";
import { useAppDispatch, useRootState } from "./index";
import {
  thrunkMemberChangeInfo,
  thrunkMembersProfile,
  thrunkWorkgroupFavorite,
  trunkFavoriteMeetingList,
  trunkGetMemberList,
  trunkGetTotalFavoriteItems,
  trunkGetWorkgroupDetail,
  trunkGetWorkgroupFullList,
  trunkGetWorkgroupSimpleList,
  trunkGetWorkspaceConfig,
  trunkOrgansTree,
  trunkUpdateStarItemFavorite,
  trunkWorkgroupCreate,
  trunkUpdateWorkgroupInfo,
  trunkWorkgroupSideList,
  trunkResendInviteMail,
  trunGetWorkgroupNoti,
  trunkUpdateWorkgroupNoti,
  thurnkWorkgroupUpdateStatus,
  thrunkMemberWithdraw,
  trunkWorkgroupHomeDashboard,
  trunkSetMeetingFavorite,
  trunkGetMyDashboardSuggestion,
  trunkGetMyDashboardTasks,
  trunkGetWorkgroupCalendarCount,
  trunkGetWorkgroupCalendarDetail,
  trunkGetRecentHistoryWorkgroupList,
  trunkRemoveTaskFromList,
  trunkDeleteMember,
  trunkCheckCheckoutUser,
} from "../store/workgroup";
import {
  FavoriteItemListReqDto,
  MemberSearchDto,
  NotificationDTO,
  Policy,
  TreeDataRequest,
  UserSimpleInfo,
  Workgroup,
  WorkgroupSearchDto,
  WorkgroupWithPermissionPolicyTarget,
} from "../../types/workgroup";

// # hooks
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useWorkgroupStore = () => {
  const workgroupState = useRootState((state) => state.workgroupStore);

  const dispatch = useAppDispatch();

  const boundGetWorkgroupSimpleList = useCallback(
    (req: WorkgroupSearchDto) =>
      dispatch(trunkGetWorkgroupSimpleList(req)).then(),
    [dispatch]
  );
  const boundWorkgroupSideList = useCallback(
    (reqDTO: WorkgroupSearchDto) =>
      dispatch(trunkWorkgroupSideList(reqDTO)).then(),
    [dispatch]
  );
  const boundGetMemberList = useCallback(
    (req: MemberSearchDto) => dispatch(trunkGetMemberList(req)).then(),
    [dispatch]
  );
  const boundGetWorkgroupFullList = useCallback(
    (req: WorkgroupSearchDto) =>
      dispatch(trunkGetWorkgroupFullList(req)).then(),
    [dispatch]
  );
  const boundSetMeetingFavorite = useCallback(
    (req: { meetingId: string; workgroupId: string; flag: boolean }) =>
      dispatch(trunkSetMeetingFavorite(req)).then(),
    [dispatch]
  );
  const boundGetTotalFavoriteItems = useCallback(
    (req: FavoriteItemListReqDto) =>
      dispatch(trunkGetTotalFavoriteItems(req)).then(),
    [dispatch]
  );
  const boundWorkgroupHomeDashboard = useCallback(
    (workgrupId: string) =>
      dispatch(trunkWorkgroupHomeDashboard(workgrupId)).then(),
    [dispatch]
  );
  const boundGetWorkgroupDetail = useCallback(
    (req: { workgroupId: string }) =>
      dispatch(trunkGetWorkgroupDetail(req)).then(),
    [dispatch]
  );
  const boundUpdateStarItemFavorite = useCallback(
    (req: { syncIds: string[]; flag: boolean }) =>
      dispatch(trunkUpdateStarItemFavorite(req)).then(),
    [dispatch]
  );
  const boundWorkgroupFavorite = useCallback(
    (req: {
      workgroupId: string;
      users: {
        userId: string;
        favorite: string;
      }[];
    }) => dispatch(thrunkWorkgroupFavorite(req)).then(),
    [dispatch]
  );
  const boundMembersProfile = useCallback(
    (userId: string) => dispatch(thrunkMembersProfile(userId)).then(),
    [dispatch]
  );
  const boundMemberChanegInfo = useCallback(
    (users: UserSimpleInfo[]) => dispatch(thrunkMemberChangeInfo(users)).then(),
    [dispatch]
  );
  const boundFavoriteMeetingList = useCallback(
    (req: FavoriteItemListReqDto) =>
      dispatch(trunkFavoriteMeetingList(req)).then(),
    [dispatch]
  );
  const boundOrgansTree = useCallback(
    (treeDataRequest: TreeDataRequest) =>
      dispatch(trunkOrgansTree(treeDataRequest)).then(),
    [dispatch]
  );
  const boundGetWorkspaceConfig = useCallback(
    () => dispatch(trunkGetWorkspaceConfig()).then(),
    [dispatch]
  );
  const boundWorkgroupCreate = useCallback(
    (workgroupRequest: WorkgroupWithPermissionPolicyTarget) =>
      dispatch(trunkWorkgroupCreate(workgroupRequest)).then(),
    [dispatch]
  );
  const boundUpdateWorkgroupInfo = useCallback(
    (req: {
      workgroupId: string;
      managerId?: string;
      workgroup?: Workgroup;
      policy?: Policy;
    }) => dispatch(trunkUpdateWorkgroupInfo(req)).then(),
    [dispatch]
  );
  const boundResendInviteMail = useCallback(
    (workgroupId: string, userId: string) =>
      dispatch(trunkResendInviteMail({ workgroupId, userId })).then(),
    [dispatch]
  );
  const boundGetWorkgroupNoti = useCallback(
    (workgroupId: string) => dispatch(trunGetWorkgroupNoti(workgroupId)).then(),
    [dispatch]
  );
  const boundUpdateWorkgroupNoti = useCallback(
    (workgroupId: string, req: NotificationDTO) =>
      dispatch(trunkUpdateWorkgroupNoti({ workgroupId, req })).then(),
    [dispatch]
  );
  const boundWorkgroupUpdateStatus = useCallback(
    (req: { workgroupId: string; status: number }) =>
      dispatch(thurnkWorkgroupUpdateStatus(req)).then(),
    [dispatch]
  );
  const boundMemberWithdraw = useCallback(
    (req: {
      workgroupId: string;
      users: {
        userId: string;
        userType: number;
        withdrawYN: string;
      }[];
    }) => dispatch(thrunkMemberWithdraw(req)).then(),
    [dispatch]
  );
  const boundGetMyDashboardTasks = useCallback(
    () => dispatch(trunkGetMyDashboardTasks()).then(),
    [dispatch]
  );
  const boundRemoveTaskFromList = useCallback(
    (requestId: string) => dispatch(trunkRemoveTaskFromList(requestId)).then(),
    [dispatch]
  );
  const boundGetMyDashboardSuggestion = useCallback(
    () => dispatch(trunkGetMyDashboardSuggestion()).then(),
    [dispatch]
  );
  const boundGetWorkgroupCalendarCount = useCallback(
    (req: {
      workgroupId: string;
      date: number;
      startDate?: number;
      endDate?: number;
      offset: number;
    }) => dispatch(trunkGetWorkgroupCalendarCount(req)).then(),
    [dispatch]
  );
  const boundGetWorkgroupCalendarDetail = useCallback(
    (req: { workgroupId: string; date: number }) =>
      dispatch(trunkGetWorkgroupCalendarDetail(req)).then(),
    [dispatch]
  );
  const bopundGetRecentHistoryWorkgroupList = useCallback(
    (req: { startDate: number; endDate: number }) =>
      dispatch(trunkGetRecentHistoryWorkgroupList(req)).then(),
    [dispatch]
  );

  const boundDeleteMember = useCallback(
    (usreId: string) => dispatch(trunkDeleteMember(usreId)).then(),
    [dispatch]
  );
  const bopundCheckCheckoutUser = useCallback(
    (userIds: string[], workgroupId?: string) =>
      dispatch(trunkCheckCheckoutUser({ userIds, workgroupId })).then(),
    [dispatch]
  );
  return {
    workgroupState,
    getWorkgroupSimpleList: boundGetWorkgroupSimpleList,
    getWorkgroupSideList: boundWorkgroupSideList,
    getWorkgroupFullList: boundGetWorkgroupFullList,
    getTotalFavoriteItems: boundGetTotalFavoriteItems,
    getMemberList: boundGetMemberList,
    getWorkgroupDetail: boundGetWorkgroupDetail,
    updateStarItemFavorite: boundUpdateStarItemFavorite,
    updateWorkgroupFavorite: boundWorkgroupFavorite,
    getMembersProfile: boundMembersProfile,
    changeMemberInfo: boundMemberChanegInfo,
    getOrgansTree: boundOrgansTree,
    getFavoriteMeetingList: boundFavoriteMeetingList,
    getWorkspaceConfig: boundGetWorkspaceConfig,
    createNewWorkgroup: boundWorkgroupCreate,
    updateWorkgroupInfo: boundUpdateWorkgroupInfo,
    resendInviteMail: boundResendInviteMail,
    getWorkgroupNoti: boundGetWorkgroupNoti,
    updateWorkgroupNoti: boundUpdateWorkgroupNoti,
    getWorkgroupUpdateStatus: boundWorkgroupUpdateStatus,
    withdrawMember: boundMemberWithdraw,
    getWorkgroupHomeDashboard: boundWorkgroupHomeDashboard,
    setMeetingFavorite: boundSetMeetingFavorite,
    getMyDashboardSuggestion: boundGetMyDashboardSuggestion,
    getMyDashboardTasks: boundGetMyDashboardTasks,
    removeTaskFromList: boundRemoveTaskFromList,
    getWorkgroupCalendarCount: boundGetWorkgroupCalendarCount,
    getWorkgroupCalendarDetail: boundGetWorkgroupCalendarDetail,
    getRecentHistoryWorkgroupList: bopundGetRecentHistoryWorkgroupList,
    deleteMember: boundDeleteMember,
    checkCheckoutUser: bopundCheckCheckoutUser,
  };
};

export default useWorkgroupStore;
